import type { MemoUser, UUID, WorkspaceMember } from "@/lib/types";

/**
 * Every face the Protokoll may need to draw, keyed by user id.
 *
 * 🔴 THE POOL IS THE ASSIGNMENT POOL, NOT THE ATTENDANCE ROSTER. `memo.participants` is who
 * was IN the meeting; it is emphatically NOT who a to-do can belong to. The `@`-mention pool
 * that mints those to-dos is deliberately much wider (`MemoPage`: workspace members ∪ memo
 * participants ∪ SERIES participants ∪ editors), because a Besprechungsserie spans workspaces
 * — and because assigning work to someone who missed the meeting is the most ordinary thing
 * there is: they were on holiday, and the room decided it is theirs when they get back.
 *
 * Resolve a face from the roster instead and that person renders as NOTHING. A to-do with no
 * avatar reads as a to-do with no owner — the one fact the line exists to carry. That was the
 * bug: a colleague on vacation was assigned two to-dos, was a participant of the SERIES but
 * (correctly) unchecked from that meeting's roster, and both rows drew blank.
 *
 * So this takes the SAME `WorkspaceMember[]` union that feeds the mention picker. The overview
 * table and the mention picker cannot disagree about who exists, because they are handed the
 * same list. Do not rebuild a second pool from `memo.participants` — that is precisely the
 * defect this function exists to make unrepresentable.
 */
export function memoPeopleById(members: WorkspaceMember[]): Map<UUID, MemoUser> {
  const byId = new Map<UUID, MemoUser>();
  for (const m of members) {
    byId.set(m.user_id, {
      id: m.user_id,
      display_name: m.display_name,
      username: m.username,
      avatar_url: m.avatar_url,
      department_workspace_id: m.department_workspace_id ?? null,
      department_color: m.department_color ?? null,
    });
  }
  return byId;
}
